// Photo Management
import { resizeImageToMultipleSizes } from './image_resizer.js';
import { UploadQueue } from './upload_queue.js';

export class PhotoManager {
  constructor(stateManager, uiManager) {
    this.stateManager = stateManager;
    this.uiManager = uiManager;
    this.editorState = stateManager.getEditorState();
    this.currentFolderId = null;
    this.photoService = stateManager.photoService;
    this.uploadQueue = new UploadQueue();
  }

  /**
   * Get current photos for active folder
   */
  getPhotos() {
    if (!this.currentFolderId) return [];
    return this.photoService.getPhotosForFolder(this.currentFolderId);
  }
  
  /**
   * Get photo by ID
   */
  getPhotoById(photoId) {
    return this.photoService.getPhotoById(photoId);
  }

  /**
   * Handle photo click (Lightroom-style selection)
   */
  handlePhotoClick(photo, event) {
    const state = this.editorState.get();
    const activePhotoId = state.activePhotoId;
    
    if (event.shiftKey && activePhotoId) {
      // Shift+Click: Select range from active photo to clicked photo
      const photos = this.getPhotos();
      const activeIndex = photos.findIndex(p => p.id === activePhotoId);
      const clickedIndex = photos.findIndex(p => p.id === photo.id);
      
      const start = Math.min(activeIndex, clickedIndex);
      const end = Math.max(activeIndex, clickedIndex);
      
      // Collect range of photo IDs
      const rangeIds = [];
      for (let i = start; i <= end; i++) {
        rangeIds.push(photos[i].id);
      }
      
      this.editorState.batch(() => {
        this.editorState.setActivePhoto(photo.id, photo);
        this.editorState.addToPhotoSelection(rangeIds);
      });
    } else if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd+Click: Toggle selection and set as active
      this.editorState.batch(() => {
        this.editorState.setActivePhoto(photo.id, photo);
        this.editorState.togglePhotoSelection(photo.id);
      });
    } else {
      // Plain click: Clear selection, select this photo, make it active
      this.editorState.batch(() => {
        this.editorState.setActivePhoto(photo.id, photo);
        this.editorState.selectPhotoRange([photo.id]);
      });
    }
  }

  /**
   * Edit a photo (show on Edit page)
   * Now supports lazy loading - will load full photo data if not yet cached
   */
  async editPhoto(photo, updateUrl = true, smoothScroll = false) {
    const state = this.editorState.get();
    const selectedPhotoIds = state.selectedPhotoIds;
    
    // Check if photo has full data loaded
    const hasFullData = this.photoService.hasFullData(photo.id);
    let fullPhoto = photo;
    
    // If photo only has basic metadata, load full data
    if (!hasFullData) {
      // Try to get from service first (might be cached)
      const cachedPhoto = this.photoService.getPhotoById(photo.id);
      if (cachedPhoto && this.photoService.hasFullData(photo.id)) {
        fullPhoto = cachedPhoto;
      } else {
        // Not in cache with full data - load it
        // Don't use loading overlay, let UI handle inline loading indicators
        try {
          console.log(`[PhotoManager] Loading full data for photo ${photo.id}`);
          const photos = await this.photoService.getFullInfo([photo.id]);
          if (photos.length > 0) {
            fullPhoto = photos[0];
          }
        } catch (error) {
          console.error('[PhotoManager] Error loading photo data:', error);
          // Continue with basic metadata if loading fails
        }
      }
    } else {
      fullPhoto = this.photoService.getPhotoById(photo.id);
    }
    
    // Prioritize uploads if still in progress
    if (this.uploadQueue.hasPendingUploads(fullPhoto.id)) {
      console.log(`[PhotoManager] Prioritizing uploads for photo ${fullPhoto.id}`);
      this.uploadQueue.prioritizePhoto(fullPhoto.id);
    }
    
    // Prioritize embedding generation if still pending
    if (fullPhoto.processing_status === 'pending') {
      console.log(`[PhotoManager] Prioritizing embedding generation for photo ${fullPhoto.id}`);
      this.prioritizeEmbeddings(fullPhoto.id).catch(error => {
        console.error('[PhotoManager] Error prioritizing embeddings:', error);
      });
    }
    
    // Update state in batch - reactive listeners will handle UI updates
    this.editorState.batch(() => {
      // Set active photo and load its sliders
      this.editorState.setActivePhoto(fullPhoto.id, fullPhoto);
      this.editorState.set({ 
        currentPage: 'edit'
      });
      
      // Only update selections if this photo isn't already selected
      if (!selectedPhotoIds.has(fullPhoto.id)) {
        this.editorState.selectPhotoRange([fullPhoto.id]);
      }
    });
    
    // Update URL if needed
    if (updateUrl) {
      this.stateManager.updateUrl('edit', fullPhoto);
    }
  }
  
  /**
   * Prioritize a photo's embedding generation
   */
  async prioritizeEmbeddings(photoId) {
    try {
      await fetch(`/api/v1/photos/${photoId}/prioritize`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('[PhotoManager] Error prioritizing embeddings:', error);
      throw error;
    }
  }

  /**
   * Navigate to previous photo
   */
  navigatePrevious() {
    const state = this.editorState.get();
    const activePhotoId = state.activePhotoId;
    const photos = this.getPhotos();
    if (!activePhotoId || photos.length === 0) return;
    
    const currentIndex = photos.findIndex(p => p.id === activePhotoId);
    if (currentIndex > 0) {
      this.editPhoto(photos[currentIndex - 1]);
    }
  }

  /**
   * Navigate to next photo
   */
  navigateNext() {
    const state = this.editorState.get();
    const activePhotoId = state.activePhotoId;
    const photos = this.getPhotos();
    if (!activePhotoId || photos.length === 0) return;
    
    const currentIndex = photos.findIndex(p => p.id === activePhotoId);
    if (currentIndex < photos.length - 1) {
      this.editPhoto(photos[currentIndex + 1]);
    }
  }

  /**
   * Navigate to previous photo and add to selection
   */
  async navigatePreviousWithSelection() {
    const state = this.editorState.get();
    const activePhotoId = state.activePhotoId;
    const photos = this.getPhotos();
    if (!activePhotoId || photos.length === 0) return;
    
    const currentIndex = photos.findIndex(p => p.id === activePhotoId);
    if (currentIndex > 0) {
      const prevPhoto = photos[currentIndex - 1];
      
      // Use editPhoto to handle lazy loading, then add to selection
      await this.editPhoto(prevPhoto, false, false);
      this.editorState.addToPhotoSelection([prevPhoto.id]);
      this.stateManager.updateUrl('edit', prevPhoto);
    }
  }

  /**
   * Navigate to next photo and add to selection
   */
  async navigateNextWithSelection() {
    const state = this.editorState.get();
    const activePhotoId = state.activePhotoId;
    const photos = this.getPhotos();
    if (!activePhotoId || photos.length === 0) return;
    
    const currentIndex = photos.findIndex(p => p.id === activePhotoId);
    if (currentIndex < photos.length - 1) {
      const nextPhoto = photos[currentIndex + 1];
      
      // Use editPhoto to handle lazy loading, then add to selection
      await this.editPhoto(nextPhoto, false, false);
      this.editorState.addToPhotoSelection([nextPhoto.id]);
      this.stateManager.updateUrl('edit', nextPhoto);
    }
  }

  /**
   * Navigate with arrow keys on Photos page
   */
  navigatePhotosGrid(direction, shiftKey = false) {
    const state = this.editorState.get();
    const activePhotoId = state.activePhotoId;
    const photos = this.getPhotos();
    if (!activePhotoId || photos.length === 0) return;
    
    const activeIndex = photos.findIndex(p => p.id === activePhotoId);
    if (activeIndex === -1) return;
    
    // Get grid columns from virtual scroller
    let gridColumns = 1;
    if (this.uiManager.virtualPhotoGrid) {
      gridColumns = this.uiManager.virtualPhotoGrid.columnsCount || 1;
    } else {
      // Fallback: calculate the same way virtual scroller does
      const photoGrid = document.getElementById('photo-grid');
      if (photoGrid) {
        const containerWidth = photoGrid.offsetWidth;
        const minItemWidth = 150; // Match virtual scroller
        const gap = 16; // Match virtual scroller (gap-4)
        gridColumns = Math.max(1, Math.floor((containerWidth + gap) / (minItemWidth + gap)));
      }
    }
    
    let newIndex = activeIndex;
    
    switch (direction) {
      case 'left':
        if (activeIndex > 0) newIndex = activeIndex - 1;
        break;
      case 'right':
        if (activeIndex < photos.length - 1) newIndex = activeIndex + 1;
        break;
      case 'up':
        newIndex = Math.max(0, activeIndex - gridColumns);
        break;
      case 'down':
        newIndex = Math.min(photos.length - 1, activeIndex + gridColumns);
        break;
    }
    
    if (newIndex !== activeIndex) {
      const newActivePhoto = photos[newIndex];
      
      if (shiftKey) {
        // Shift+Arrow: Expand selection (Lightroom-style)
        // Select all photos between old and new position (inclusive)
        const start = Math.min(activeIndex, newIndex);
        const end = Math.max(activeIndex, newIndex);
        const rangeIds = [];
        for (let i = start; i <= end; i++) {
          rangeIds.push(photos[i].id);
        }
        
        this.editorState.batch(() => {
          this.editorState.set({ activePhotoId: newActivePhoto.id });
          this.editorState.addToPhotoSelection(rangeIds);
        });
      } else {
        // Plain Arrow: Replace selection
        this.editorState.batch(() => {
          this.editorState.set({ activePhotoId: newActivePhoto.id });
          this.editorState.selectPhotoRange([newActivePhoto.id]);
        });
      }
      
      return true; // Indicate navigation occurred
    }
    
    return false;
  }

  /**
   * Select all photos
   */
  selectAll() {
    const photos = this.getPhotos();
    const photoIds = photos.map(p => p.id);
    this.editorState.selectPhotoRange(photoIds);
    
    const state = this.editorState.get();
    if (!state.activePhotoId && photos.length > 0) {
      this.editorState.set({ activePhotoId: photos[0].id });
    }
  }

  /**
   * Deselect all photos
   */
  deselectAll() {
    this.editorState.batch(() => {
      this.editorState.clearPhotoSelection();
      this.editorState.set({ activePhotoId: null });
    });
  }

  /**
   * Delete selected photos
   */
  async deleteSelected() {
    const state = this.editorState.get();
    const selectedPhotoIds = state.selectedPhotoIds;
    if (selectedPhotoIds.size === 0) return;
    
    const photoIds = Array.from(selectedPhotoIds);
    
    for (const photoId of photoIds) {
      try {
        await fetch(`/api/v1/photos/${photoId}`, { method: 'DELETE' });
        // Remove from service cache
        this.photoService.removePhoto(photoId);
      } catch (error) {
        console.error(`Error deleting photo ${photoId}:`, error);
      }
    }
    
    this.editorState.clearPhotoSelection();
    
    return true;
  }

  /**
   * Upload files to a folder with browser-side resizing and prioritized uploads
   */
  async uploadFiles(files, folderId, onProgress, onComplete, onUploadProgress) {
    const uploadedPhotos = [];
    
    console.log(`[PhotoManager] Starting upload of ${files.length} files`);
    
    // Track upload completion for all photos and sizes
    const sizes = [256, 512, 1024, 768, 'original'];
    const totalUploads = files.length * sizes.length;
    let completedUploads = 0;
    
    // Track completion by size (for current size progress)
    const completionBySize = {
      256: { completed: 0, total: files.length },
      512: { completed: 0, total: files.length },
      1024: { completed: 0, total: files.length },
      768: { completed: 0, total: files.length },
      original: { completed: 0, total: files.length }
    };
    
    // Step 1: Create all photo records and resize all images BEFORE starting uploads
    const resizePromises = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Create photo record in backend (no file attached yet)
        const photo = await this.createPhotoRecord(file.name, folderId);
        uploadedPhotos.push(photo);
        
        // Start resizing in parallel (don't await yet)
        const resizePromise = this.resizeAndQueueUpload(
          photo.id, 
          file, 
          [256, 512, 1024, 768], 
          true, // defer processing
          (size) => {
            completedUploads++;
            completionBySize[size].completed++;
            
            console.log(`[PhotoManager] Upload progress: ${completedUploads}/${totalUploads} (${size}: ${completionBySize[size].completed}/${completionBySize[size].total})`);
            
            // Report detailed progress to UI
            if (onUploadProgress) {
              const currentSizeName = this.getSizeLabel(size);
              onUploadProgress(
                completedUploads, 
                totalUploads, 
                currentSizeName, 
                completionBySize[size].completed, 
                completionBySize[size].total
              );
            }
            
            // When ALL uploads for ALL photos complete, notify
            if (completedUploads === totalUploads && onComplete) {
              console.log(`[PhotoManager] All uploads complete!`);
              onComplete();
            }
          }
        );
        
        resizePromises.push(resizePromise);
        
        // Call progress callback immediately after creating record
        // This allows the UI to show the photo placeholder right away
        if (onProgress) {
          onProgress(i + 1, files.length, photo);
        }
        
      } catch (error) {
        console.error('Error creating photo record:', file.name, error);
      }
    }
    
    // Step 2: Wait for all resizing to complete
    await Promise.all(resizePromises);
    
    // Step 3: Now start processing the upload queue (sorted by size across all photos)
    console.log(`[PhotoManager] All ${files.length} photo records created and resized - starting uploads (0/${totalUploads})`);
    this.uploadQueue.startProcessing();
    
    return uploadedPhotos;
  }
  
  /**
   * Get human-readable label for size
   */
  getSizeLabel(size) {
    const labels = {
      256: 'small thumbnails',
      512: 'medium thumbnails',
      1024: 'large thumbnails',
      768: 'preview images',
      original: 'original images'
    };
    return labels[size] || size;
  }
  
  /**
   * Resize image and add to upload queue
   * @param {boolean} deferProcessing - If true, don't start uploads yet (for batching)
   */
  async resizeAndQueueUpload(photoId, file, sizes, deferProcessing = false, onSizeComplete) {
    // Resize image to all sizes
    const resizedBlobs = await resizeImageToMultipleSizes(file, sizes);
    
    // Keep original file
    resizedBlobs['original'] = file;
    
    // Add to upload queue with deferred processing
    this.uploadQueue.addPhotoUploads(photoId, resizedBlobs, 0, deferProcessing);
    
    // Listen for upload progress
    this.uploadQueue.onProgress(photoId, (size, progress) => {
      console.log(`Photo ${photoId} size ${size}: ${(progress * 100).toFixed(0)}%`);
    });
    
    this.uploadQueue.onComplete(photoId, (size, updatedPhoto) => {
      console.log(`Photo ${photoId} size ${size}: Complete`);
      
      // Update PhotoService with new URLs
      if ((size === 256 || size === 512 || size === 1024) && updatedPhoto) {
        console.log(`[PhotoManager] Updating PhotoService for photo ${photoId} with ${size}px URL`);
        this.photoService.updatePhoto(photoId, updatedPhoto);
      }
      
      // Notify with size so we can track per-size progress
      if (onSizeComplete) {
        onSizeComplete(size);
      }
    });
    
    this.uploadQueue.onError(photoId, (size, error) => {
      console.error(`Photo ${photoId} size ${size}: Error`, error);
      // Count errors as "complete" so progress doesn't get stuck
      if (onSizeComplete) {
        onSizeComplete(size);
      }
    });
  }
  
  /**
   * Create photo record without file attachments
   */
  async createPhotoRecord(filename, folderId) {
    const response = await fetch('/api/v1/photos/create_with_variants', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        folder_id: folderId,
        filename: filename
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to create photo record: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Check if any photos are currently processing
   */
  hasProcessingPhotos() {
    const photos = this.getPhotos();
    return photos.some(photo => 
      photo.processing_status === 'pending' || 
      photo.processing_status === 'processing'
    );
  }
}

